import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';

import { filter, skip, Subscription, take } from 'rxjs';

import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import {
  isBackgroundRefreshPending,
  isSearchResultReady,
  isVendorSyncComplete,
} from '../../../services/search-polling/osint-job-poll-response.util';
import { LeakSearchService } from '../../../services/leak-search/leak-search.service';
import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import {
  isBlankLeakValue,
  LeakEmailItemsPipe,
  LeakFieldBlankPipe,
} from '../../../pipes/leak-email-items.pipe';

@Component({
  selector: 'app-leak-email-search-result',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    HeaderComponent,
    SidebarComponent,
    LeakEmailItemsPipe,
    LeakFieldBlankPipe,
  ],
  templateUrl: './leak-email-search-result.component.html',
  styleUrl: './leak-email-search-result.component.css',
})
export class LeakEmailSearchResultComponent implements OnInit, OnDestroy {
  jobId: string = '';
  searchId: string = '';

  selectedSearchResult: any;

  searchResultCount: number = 0;
  searchResults: any[] = [];
  searchResultsFiltered: any[] = [];

  loadingCompletedSearchResult: boolean = false;
  loadingPolledSearchResult: boolean = false;
  backgroundRefreshPending: boolean = false;

  jobResultSubscription?: Subscription;
  vendorSyncSubscription?: Subscription;

  /**
   * Computed property to determine if we should show the main loader
   * Show loader until we have actual results to display
   */
  get isLoadingResults(): boolean {
    const hasResults =
      this.searchResults?.length > 0 || this.searchResultsFiltered?.length > 0;
    return (
      (this.loadingCompletedSearchResult || this.loadingPolledSearchResult) &&
      !hasResults
    );
  }

  searchTerm: string = '';
  notes: string = '';

  constructor(
    private router: Router,
    private searchPollingService: SearchPollingService,
    private leakSearchService: LeakSearchService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit() {
    this.selectedSearchResult = history.state?.searchResult;
    this.searchId = this.selectedSearchResult.leak_search_id;

    // console.log('SELECTED SEARCH RESULT: ', this.selectedSearchResult);

    if (!this.selectedSearchResult) {
      // console.warn('NO SEARCH RESULT FOUND IN HISTORY STATE');
      this.router.navigate(['/leak-email-search']);
      return;
    }

    this.backgroundRefreshPending = Boolean(
      this.selectedSearchResult.background_refresh_pending,
    );

    if (this.selectedSearchResult.done) {
      this.getCompletedSearchResult();
      if (this.backgroundRefreshPending) {
        this.watchVendorSyncThenReload();
      }
    } else {
      this.pollForSearchResult();
    }
  }

  ngOnDestroy() {
    this.jobResultSubscription?.unsubscribe();
    this.vendorSyncSubscription?.unsubscribe();
  }

  onBackButtonClick() {
    window.history.back();
  }

  getCompletedSearchResult() {
    const payload = {
      type: 'email',
      searchId: this.searchId,
    };
    const hasVisibleResults = this.searchResults.length > 0;
    if (!hasVisibleResults) {
      this.loadingCompletedSearchResult = true;
    }

    this.leakSearchService
      .getCompletedSearchResult(payload.type, payload.searchId)
      .subscribe({
        next: (response) => {
          const rawSearchResultData = response?.data;
          const refreshPending = Boolean(
            rawSearchResultData?.background_refresh_pending,
          );
          this.backgroundRefreshPending = refreshPending;

          this.searchResultCount = rawSearchResultData?.data_list?.length ?? 0;
          this.searchResults = rawSearchResultData?.data_list ?? [];
          this.searchResults.forEach((result) => {
            result.leak_data.forEach((leak_data: any) => {
              if (
                leak_data.address ||
                leak_data.address2 ||
                leak_data.address3
              ) {
                leak_data.address = [
                  leak_data.address2,
                  leak_data.address3,
                  leak_data.address,
                ]
                  .filter((v) => v && v.trim() !== '') // remove empty, null, undefined, blank strings
                  .join(', ');
                delete leak_data.address2;
                delete leak_data.address3;
              }

              Object.keys(leak_data).forEach((key) => {
                if (isBlankLeakValue(leak_data[key])) {
                  delete leak_data[key];
                }
              });
            });
          });
          this.searchResultsFiltered = JSON.parse(
            JSON.stringify(this.searchResults),
          );
          this.notes = rawSearchResultData?.notes || '';

          this.selectedSearchResult = {
            ...(this.selectedSearchResult || {}),
            done: true,
            background_refresh_pending: refreshPending,
            status: refreshPending ? 'refreshing' : 'completed',
          };

          this.loadingCompletedSearchResult = false;
          this.loadingPolledSearchResult = false;

          if (refreshPending) {
            this.watchVendorSyncThenReload();
          }
        },
        error: (error) => {
          console.error(error);
          this.loadingCompletedSearchResult = false;
          this.loadingPolledSearchResult = false;
        },
      });
  }

  pollForSearchResult() {
    const jobId = this.selectedSearchResult.job_id;

    this.ensureLeakBackgroundPolling();

    this.jobResultSubscription?.unsubscribe();
    if (!this.searchResults.length) {
      this.loadingPolledSearchResult = true;
    }

    this.jobResultSubscription = this.searchPollingService
      .getJobResult(jobId)
      .pipe(skip(1))
      .subscribe({
        next: (response) => {
          if (!response) {
            return;
          }

          const refreshPending = isBackgroundRefreshPending(response);
          this.backgroundRefreshPending = refreshPending;

          this.selectedSearchResult = {
            ...this.selectedSearchResult,
            done: response.done || false,
            status: response.status || 'pending',
            background_refresh_pending: refreshPending,
          };

          if (isSearchResultReady(response) && !this.searchResults.length) {
            this.getCompletedSearchResult();
          }

          if (isVendorSyncComplete(response)) {
            this.backgroundRefreshPending = false;
            this.jobResultSubscription?.unsubscribe();
            this.getCompletedSearchResult();
          }
        },
        error: (error) => {
          console.error(error);
          this.loadingPolledSearchResult = false;
        },
      });
  }

  private watchVendorSyncThenReload() {
    const jobId = this.selectedSearchResult?.job_id;
    if (!jobId) {
      return;
    }

    this.ensureLeakBackgroundPolling();
    this.vendorSyncSubscription?.unsubscribe();
    this.vendorSyncSubscription = this.searchPollingService
      .getJobResult(jobId)
      .pipe(
        filter((s) => !!s && isVendorSyncComplete(s)),
        take(1),
      )
      .subscribe(() => {
        this.backgroundRefreshPending = false;
        if (this.selectedSearchResult) {
          this.selectedSearchResult = {
            ...this.selectedSearchResult,
            background_refresh_pending: false,
            status: 'completed',
            done: true,
          };
        }
        this.getCompletedSearchResult();
      });
  }

  private ensureLeakBackgroundPolling() {
    const jobId = this.selectedSearchResult?.job_id;
    if (!jobId) {
      return;
    }
    this.searchPollingService.ensureBackgroundPolling(
      [
        {
          job_id: jobId,
          searchType: 'leak-email',
          label: this.selectedSearchResult?.query || jobId,
        },
      ],
      3 * 1000,
    );
  }

  onSearchTermChange() {
    const searchTermLower = this.searchTerm?.toLowerCase().trim() ?? '';

    if (!searchTermLower) {
      this.searchResultsFiltered =
        this.searchResults?.length > 0
          ? JSON.parse(JSON.stringify(this.searchResults))
          : [];
      return;
    }

    if (!this.searchResults?.length) {
      this.searchResultsFiltered = [];
      return;
    }

    const matchesInLeakData = (result: any) =>
      (result.leak_data ?? []).some((entry: any) =>
        Object.entries(entry ?? {}).some(([key, value]) => {
          if (key === 'image_url') return false;
          const str = value != null ? String(value).toLowerCase() : '';
          return str.includes(searchTermLower);
        }),
      );
    const matchesDescription = (result: any) =>
      String(result?.description ?? '')
        .toLowerCase()
        .includes(searchTermLower);
    const matchesName = (result: any) =>
      String(result?.name ?? '')
        .toLowerCase()
        .includes(searchTermLower);

    this.searchResultsFiltered = this.searchResults.filter(
      (result) =>
        matchesInLeakData(result) ||
        matchesDescription(result) ||
        matchesName(result),
    );
  }

  // onExportOptionClick(exportFormat: string) {
  //   this.exportSearchResult(exportFormat);
  // }

  // exportSearchResult(format: string) {
  //   this.leakSearchService
  //     .exportSearchResult('email', this.searchId, format)
  //     .subscribe({
  //       next: (response) => {
  //         // console.log('EXPORT OSINT SEARCH RESULT API RESPONSE: ', response);
  //         this.saveFile(response?.data);
  //       },
  //       error: (error) => {
  //         console.error(error);
  //       },
  //     });
  // }

  // saveFile(fileInfo: any) {
  //   const { fileName, mimeType, base64 } = fileInfo;

  //   const linkSource = `data:${mimeType};base64,${base64}`;
  //   const saveLink = document.createElement('a');

  //   saveLink.href = linkSource;
  //   saveLink.download = fileName;
  //   saveLink.click();
  // }

  onLogoError(event: Event) {
    const placeholderIcon = '/assets/icons/placeholder-icon.png';

    const target = event.target as HTMLImageElement;
    target.src = placeholderIcon;
  }

  onUpdateNotesButtonClick() {
    const resultId = this.searchResultsFiltered?.[0]?.result_id;

    if (!this.notes.trim() || !resultId) {
      console.warn('Please enter valid notes');
      return;
    }

    const payload = {
      notes: this.notes.trim(),
      // uploads: [] // Add if needed later
    };

    this.leakSearchService.updateNotes(resultId, payload).subscribe({
      next: () => {
        this.snackBar.open('Note has been updated successfully.', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-success'],
        });
      },
      error: () => {
        this.snackBar.open('Failed to update note.', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-error'],
        });
      },
    });
  }

  formatFieldLabel(key: unknown): string {
    // Convert to string if not already
    const keyStr = String(key || '');

    // Replace underscores with spaces
    let formatted = keyStr.replace(/_/g, ' ');

    // Replace camelCase with spaces (add space before capital letters)
    formatted = formatted.replace(/([a-z])([A-Z])/g, '$1 $2');

    // Capitalize first letter of each word
    return formatted
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Get the appropriate icon configuration for a given field key
   * @param key - The field key from the API response
   * @returns Object with icon name and gradient colors
   */
  getFieldIcon(key: string): { icon: string; gradient: string } {
    const iconMap: { [key: string]: { icon: string; gradient: string } } = {
      email: {
        icon: 'email',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      },
      address: {
        icon: 'location_on',
        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      },
      password: {
        icon: 'lock',
        gradient: 'linear-gradient(135deg, #ffa751 0%, #ffe259 100%)',
      },
      name: {
        icon: 'person',
        gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      },
      phone: {
        icon: 'phone',
        gradient: 'linear-gradient(135deg, #96fbc4 0%, #f9f586 100%)',
      },
      mobile: {
        icon: 'smartphone',
        gradient: 'linear-gradient(135deg, #81fbb8 0%, #28c76f 100%)',
      },
      username: {
        icon: 'account_circle',
        gradient: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
      },
      user: {
        icon: 'person',
        gradient: 'linear-gradient(135deg, #c471f5 0%, #fa71cd 100%)',
      },
      date: {
        icon: 'calendar_today',
        gradient: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
      },
      company: {
        icon: 'business',
        gradient: 'linear-gradient(135deg, #f83600 0%, #f9d423 100%)',
      },
      organization: {
        icon: 'business',
        gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      },
      website: {
        icon: 'language',
        gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      },
      url: {
        icon: 'link',
        gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      },
      city: {
        icon: 'location_city',
        gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
      },
      country: {
        icon: 'public',
        gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      },
      zip: {
        icon: 'markunread_mailbox',
        gradient: 'linear-gradient(135deg, #feb692 0%, #ea5455 100%)',
      },
      zipcode: {
        icon: 'markunread_mailbox',
        gradient: 'linear-gradient(135deg, #feb692 0%, #ea5455 100%)',
      },
      postal: {
        icon: 'markunread_mailbox',
        gradient: 'linear-gradient(135deg, #ce9ffc 0%, #7367f0 100%)',
      },
      state: {
        icon: 'map',
        gradient: 'linear-gradient(135deg, #90f7ec 0%, #32ccbc 100%)',
      },
      gender: {
        icon: 'wc',
        gradient: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
      },
      age: {
        icon: 'cake',
        gradient: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
      },
      dob: {
        icon: 'cake',
        gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      },
      birthdate: {
        icon: 'cake',
        gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      },
      job: {
        icon: 'work',
        gradient: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
      },
      title: {
        icon: 'badge',
        gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      },
      department: {
        icon: 'apartment',
        gradient: 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
      },
      salary: {
        icon: 'attach_money',
        gradient: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
      },
      income: {
        icon: 'attach_money',
        gradient: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
      },
      card: {
        icon: 'credit_card',
        gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      },
      creditcard: {
        icon: 'credit_card',
        gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
      },
      bank: {
        icon: 'account_balance',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      },
      social: {
        icon: 'share',
        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      },
      linkedin: {
        icon: 'person',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      },
      facebook: {
        icon: 'person',
        gradient: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
      },
      twitter: {
        icon: 'person',
        gradient: 'linear-gradient(135deg, #96fbc4 0%, #f9f586 100%)',
      },
      instagram: {
        icon: 'photo_camera',
        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      },
      description: {
        icon: 'description',
        gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      },
      notes: {
        icon: 'note',
        gradient: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
      },
      comment: {
        icon: 'comment',
        gradient: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
      },
      id: {
        icon: 'fingerprint',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      },
      userid: {
        icon: 'fingerprint',
        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      },
      profile: {
        icon: 'account_box',
        gradient: 'linear-gradient(135deg, #c471f5 0%, #fa71cd 100%)',
      },
      avatar: {
        icon: 'account_circle',
        gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      },
      image: {
        icon: 'image',
        gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      },
      photo: {
        icon: 'photo',
        gradient: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
      },
      ip: {
        icon: 'router',
        gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      },
      ipaddress: {
        icon: 'router',
        gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      },
      mac: {
        icon: 'devices',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      },
      device: {
        icon: 'devices',
        gradient: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
      },
      browser: {
        icon: 'public',
        gradient: 'linear-gradient(135deg, #96fbc4 0%, #f9f586 100%)',
      },
      os: {
        icon: 'computer',
        gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      },
      version: {
        icon: 'update',
        gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
      },
      status: {
        icon: 'info',
        gradient: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
      },
      type: {
        icon: 'category',
        gradient: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
      },
      category: {
        icon: 'category',
        gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      },
      tag: {
        icon: 'label',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      },
      created: {
        icon: 'schedule',
        gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      },
      updated: {
        icon: 'update',
        gradient: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
      },
      modified: {
        icon: 'edit',
        gradient: 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
      },
      verified: {
        icon: 'verified',
        gradient: 'linear-gradient(135deg, #96fbc4 0%, #f9f586 100%)',
      },
      confirmed: {
        icon: 'check_circle',
        gradient: 'linear-gradient(135deg, #81fbb8 0%, #28c76f 100%)',
      },
    };

    // Normalize the key: lowercase and remove underscores/spaces
    const keyLower = key.toLowerCase().replace(/[_\s-]/g, '');

    // Check for exact match first
    if (iconMap[keyLower]) {
      return iconMap[keyLower];
    }

    // Check if the key contains any of the mapped keys
    for (const [mapKey, iconConfig] of Object.entries(iconMap)) {
      if (keyLower.includes(mapKey) || mapKey.includes(keyLower)) {
        return iconConfig;
      }
    }

    // Default icon if no match found
    return {
      icon: 'info',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    };
  }
}
